import { useCallback, useState } from "react";

import { confirmable } from "react-confirm";

import { CheckIcon, XIcon } from "@lucide/icons";

import { handleClickForKeyboard } from "simplydo/core/accessibility";
import { Button } from "simplydo/react/ui/button";
import { Icon } from "simplydo/react/ui/icon";
import { Input } from "simplydo/react/ui/input";
import {
  Modal,
  ModalActions,
  ModalContent,
  ModalHeader,
} from "simplydo/react/ui/modal";

type PromptModalProps = {
  show?: boolean;
  dismiss?: () => void;
  title: string;
  prompt: string;
  proceed: () => void;
  onComplete: (value: string) => void;
};

const PromptModal = ({
  show,
  dismiss,
  title,
  prompt,
  proceed,
  onComplete,
}: PromptModalProps) => {
  const [value, setValue] = useState("");
  const [confirmed, setConfirmed] = useState(false);

  const complete = useCallback(() => {
    if (!value) return;

    if (!confirmed) {
      setConfirmed(true);
      onComplete(value);
      proceed();
    }
  }, [value, onComplete, proceed, confirmed]);

  return (
    <Modal
      open={!!show}
      onClose={dismiss ?? (() => {})}
      basic
      size="small"
      className="no-scroll"
      closeOnDimmerClick={false}
    >
      <ModalHeader content={title} />
      <ModalContent>
        <p>{prompt}</p>
        <Input
          fluid
          placeholder="Please enter a value"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={handleClickForKeyboard(complete, { keys: ["Enter"] })}
        />
      </ModalContent>
      <ModalActions>
        <Button
          onClick={dismiss ?? (() => {})}
          variant="outline"
          color="grey"
          style={{ color: "#fff" }}
        >
          <Icon icon={XIcon} /> Cancel
        </Button>
        <Button onClick={complete} disabled={!value} color="primary">
          <Icon icon={CheckIcon} /> Confirm
        </Button>
      </ModalActions>
    </Modal>
  );
};

export default confirmable(PromptModal);
